import { SCHEDA_PALETTE } from './theme';
import { findExercise } from './exerciseDB';
import { getSchedaCustom, getSchedaColor } from './schedaIcons';
import { normalizeHevyName } from './parsers';

// ─── AVATAR ───────────────────────────────────────────────────────────────────
function _hashStr(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}
export function getAvatarUrl(userId) {
  const n = (_hashStr(userId || 'guest') % 35) + 1;
  return `https://cdn.jsdelivr.net/gh/alohe/memojis@main/png/memo_${n}.png`;
}

export function buildColorMap(schede,sessions) {
  const names=schede.length?schede.map(s=>s.name):[...new Set(sessions.map(s=>s.workoutName).filter(Boolean))];
  const map={};
  names.forEach((n,i)=>{
    const sid=schede.find(s=>s.name===n)?.id;
    const custom=sid?getSchedaCustom(sid):{};
    map[n]=getSchedaColor(n)||custom.color||SCHEDA_PALETTE[i%SCHEDA_PALETTE.length];
  });
  return map;
}

export function estimateTDEE(profilo) {
  const weight = parseFloat(profilo?.peso) || 75;
  const age    = parseInt(profilo?.eta)    || 25;
  const bmr    = 10 * weight + 6.25 * 175 - 5 * age; // formula neutra senza genere
  const tdee   = Math.round(bmr * 1.55);
  if (profilo?.obiettivo === "cut")  return tdee - 400;
  if (profilo?.obiettivo === "bulk") return tdee + 300;
  return tdee; // recomp/default
}

export function sentimentScore(mood, sleepH, kcal, tdee) {
  if (!mood) return null;
  // Mood: 0–5 pt  (peso 50%)
  const moodPts = (mood - 1) * 1.25; // 0, 1.25, 2.5, 3.75, 5

  // Sleep: 0–3 pt (peso 30%)
  const h = parseFloat(sleepH) || 0;
  const sleepPts = h >= 8.5 ? 3 : h >= 7.5 ? 2.5 : h >= 6.5 ? 2 : h >= 5.5 ? 1 : h >= 4 ? 0.5 : 0;

  // Kcal vs TDEE: 0–2 pt (peso 20%)
  let kcalPts = 1; // default neutro se non inserito
  if (kcal && tdee) {
    const ratio = parseFloat(kcal) / tdee;
    kcalPts = ratio >= 0.95 ? 2 : ratio >= 0.75 ? 1.5 : ratio >= 0.55 ? 1 : 0.5;
  }

  return Math.min(10, Math.round((moodPts + sleepPts + kcalPts) * 10) / 10);
}

// Returns true when a session's workoutName is considered to belong to a scheda.
// Exact match wins; otherwise partial containment (case-insensitive).
export function sessionFitsScheda(workoutName, schedaName) {
  if (!workoutName || !schedaName) return false;
  const wn = workoutName.toLowerCase().trim();
  const sn = schedaName.toLowerCase().trim();
  return wn === sn || wn.includes(sn) || sn.includes(wn);
}

// Compares imported exercise names against a scheda template.
// Returns { added, removed, hasChanges } using case-insensitive name matching.
export function diffSchedaExercises(importedNames, templateExercises) {
  const tNames = (templateExercises || []).map(e => e.name.toLowerCase().trim());
  const iNames = (importedNames || []).map(n => n.toLowerCase().trim());
  const added   = importedNames.filter((_, i) => !tNames.includes(iNames[i]));
  const removed = (templateExercises || []).filter(e => !iNames.includes(e.name.toLowerCase().trim())).map(e => e.name);
  return { added, removed, hasChanges: added.length > 0 || removed.length > 0 };
}

// ─── RANK SCHEDE ─────────────────────────────────────────────────────────────
// Returns active (non-disabled) schede sorted by priority.
// Established schede (≥2 sessions) come before provisional ones (0–1 sessions).
// Within each tier: most overdue first. daysAgo=999 means never trained.
export function rankSchede(schede, sessions) {
  return schede
    .filter(sc => !sc.disabled)
    .map(sc => {
      const matching = sessions.filter(s => sessionFitsScheda(s.workoutName, sc.name));
      const last = [...matching].sort((a, b) => b.date.localeCompare(a.date))[0];
      const daysAgo = last ? Math.floor((Date.now() - new Date(last.date)) / 86400000) : 999;
      return { ...sc, daysAgo, sessionCount: matching.length };
    })
    .sort((a, b) => {
      const aEst = a.sessionCount >= 2 ? 0 : 1;
      const bEst = b.sessionCount >= 2 ? 0 : 1;
      if (aEst !== bEst) return aEst - bEst;
      return b.daysAgo - a.daysAgo;
    });
}

// ─── AUTO-ASSOCIA ESERCIZI ────────────────────────────────────────────────────
// Returns exercises that appear in ≥50% of sessions for the scheda.
// Outlier sessions (e.g. one-off "Gambe e Tricipiti") are automatically excluded.
export function autoAssociaEsercizi(schedaName, sessions) {
  const relevant = sessions.filter(s => sessionFitsScheda(s.workoutName, schedaName));
  if (!relevant.length) return [];
  const N = relevant.length;
  const countMap = {};   // canonical name → n° sessioni in cui compare
  const setsMap  = {};   // canonical name → [n° serie per sessione]
  const repsMap  = {};   // canonical name → [tutti i valori reps loggati]
  relevant.forEach(sess => {
    const seen = new Set();
    sess.exercises.forEach(ex => {
      const canonical = findExercise(normalizeHevyName(ex.name))?.name || ex.name;
      if (seen.has(canonical)) return;
      seen.add(canonical);
      countMap[canonical] = (countMap[canonical] || 0) + 1;
      (setsMap[canonical] = setsMap[canonical] || []).push(ex.sets?.length || 3);
      (ex.sets || []).forEach(s => { if (Number(s.reps) > 0) (repsMap[canonical] = repsMap[canonical] || []).push(Number(s.reps)); });
    });
  });
  const threshold = Math.max(1, Math.ceil(N * 0.5));
  return Object.entries(countMap)
    .filter(([, c]) => c >= threshold)
    .sort((a, b) => b[1] - a[1])
    .map(([name]) => {
      const ex = findExercise(name);
      const avgSets = Math.round(setsMap[name].reduce((a,b)=>a+b,0) / setsMap[name].length) || 3;
      const reps = (repsMap[name] || []).filter(r => r > 0);
      const lo = reps.length ? Math.min(...reps) : 8;
      const hi = reps.length ? Math.max(...reps) : 12;
      return { name, sets: avgSets, repMin: Math.min(lo, hi), repMax: Math.max(lo, hi), type: ex?.type || "compound" };
    });
}
